import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Copy, Download } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';

export const CodeInput = ({ value, onChange, onComplete, id = 'auth-mfa-code', containerClassName = 'enterprise-auth-otp', slotClassName = 'enterprise-auth-otp-slot' }) => (
  <InputOTP id={id} maxLength={6} inputMode="numeric" pattern="^[0-9]*$" autoComplete="one-time-code" autoFocus value={value} onChange={onChange} onComplete={onComplete} containerClassName={containerClassName} aria-label="6-digit code">
    <InputOTPGroup>
      {[0, 1, 2, 3, 4, 5].map((index) => <InputOTPSlot key={index} index={index} className={`h-11 w-11 text-lg ${slotClassName}`} />)}
    </InputOTPGroup>
  </InputOTP>
);

export const RecoveryCodeList = ({ codes, email }) => {
  const text = codes.join('\n');
  const download = () => {
    const blob = new Blob([`Qflow recovery codes for ${email || 'your account'}\nEach code works once.\n\n${text}\n`], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'qflow-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <div>
      <ul className="enterprise-auth-recovery-codes" data-testid="mfa-recovery-codes">{codes.map((code) => <li key={code}>{code}</li>)}</ul>
      <div className="enterprise-auth-recovery-actions">
        <button type="button" className="enterprise-auth-secondary" onClick={() => navigator.clipboard?.writeText(text)}><Copy size={15}/>Copy</button>
        <button type="button" className="enterprise-auth-secondary" onClick={download}><Download size={15}/>Download</button>
      </div>
    </div>
  );
};

const MfaStep = ({ mode, email, enrollment, recoveryCodes, error, busy, onVerify, onConfirmEnrollment, onFinish, onBack }) => {
  const headingRef = useRef(null);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  useEffect(() => headingRef.current?.focus(), [mode, useRecovery, Boolean(recoveryCodes)]);
  useEffect(() => { if (error) setCode(''); }, [error]);

  const submitCode = (value = code) => {
    if (value.length !== 6 || busy) return;
    if (mode === 'enroll') onConfirmEnrollment(value);
    else onVerify({ code: value });
  };

  if (recoveryCodes) {
    return (
      <div className="enterprise-auth-step">
        <p className="enterprise-auth-kicker">Two-step verification is on</p>
        <h2 ref={headingRef} tabIndex="-1">Save your recovery codes</h2>
        <p className="enterprise-auth-intro">If you lose your phone, each of these codes lets you sign in once. They will not be shown again.</p>
        <RecoveryCodeList codes={recoveryCodes} email={email} />
        <button type="button" className="enterprise-auth-primary" data-testid="mfa-finish" onClick={onFinish}>I've saved my codes</button>
      </div>
    );
  }

  if (mode === 'enroll') {
    return (
      <div className="enterprise-auth-step">
        <p className="enterprise-auth-kicker">Required by your organisation</p>
        <h2 ref={headingRef} tabIndex="-1">Set up two-step verification</h2>
        <p className="enterprise-auth-intro">Scan this code with an authenticator app such as Microsoft Authenticator, Google Authenticator or 1Password, then enter the 6-digit code it shows.</p>
        {enrollment ? <>
          <img className="enterprise-auth-qr" src={enrollment.qr_svg} alt="QR code for your authenticator app" width="180" height="180" />
          <details className="enterprise-auth-manual-key"><summary>Can't scan it?</summary><p>Enter this key in your app:</p><code data-testid="mfa-manual-key">{enrollment.secret.match(/.{1,4}/g).join(' ')}</code></details>
          <form onSubmit={(event) => { event.preventDefault(); submitCode(); }} noValidate>
            <label htmlFor="auth-mfa-code">6-digit code</label>
            <CodeInput value={code} onChange={setCode} onComplete={submitCode} />
            {error && <p className="enterprise-auth-error" role="alert">{error}</p>}
            <button className="enterprise-auth-primary" type="submit" aria-busy={busy} data-testid="mfa-submit">{busy ? 'Checking…' : 'Turn on and sign in'}</button>
          </form>
        </> : <>{error ? <p className="enterprise-auth-error" role="alert">{error}</p> : <p className="enterprise-auth-intro">Preparing your code…</p>}</>}
        <button type="button" className="enterprise-auth-back" onClick={onBack}><ArrowLeft size={16}/>Cancel sign-in</button>
      </div>
    );
  }

  return (
    <div className="enterprise-auth-step">
      <p className="enterprise-auth-kicker">Two-step verification</p>
      <h2 ref={headingRef} tabIndex="-1">{useRecovery ? 'Use a recovery code' : 'Enter your code'}</h2>
      <p className="enterprise-auth-account">{email}</p>
      {useRecovery ? (
        <form onSubmit={(event) => { event.preventDefault(); if (recoveryCode.trim() && !busy) onVerify({ recovery_code: recoveryCode.trim() }); }} noValidate>
          <label htmlFor="auth-mfa-recovery">Recovery code</label>
          <input id="auth-mfa-recovery" data-testid="mfa-recovery-input" autoComplete="off" autoFocus spellCheck="false" placeholder="xxxxx-xxxxx" value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} aria-invalid={Boolean(error)} />
          {error && <p className="enterprise-auth-error" role="alert">{error}</p>}
          <button className="enterprise-auth-primary" type="submit" aria-busy={busy}>{busy ? 'Checking…' : 'Sign in'}</button>
        </form>
      ) : (
        <form onSubmit={(event) => { event.preventDefault(); submitCode(); }} noValidate>
          <label htmlFor="auth-mfa-code">6-digit code from your authenticator app</label>
          <CodeInput value={code} onChange={setCode} onComplete={submitCode} />
          {error && <p className="enterprise-auth-error" role="alert">{error}</p>}
          <button className="enterprise-auth-primary" type="submit" aria-busy={busy} data-testid="mfa-submit">{busy ? 'Checking…' : 'Verify'}</button>
        </form>
      )}
      <div className="enterprise-auth-row-links">
        <button type="button" className="enterprise-auth-back" onClick={onBack}><ArrowLeft size={16}/>Back</button>
        <button type="button" className="enterprise-auth-link-button" onClick={() => { setUseRecovery((current) => !current); }}>{useRecovery ? 'Use authenticator code' : 'Use a recovery code'}</button>
      </div>
    </div>
  );
};

export default MfaStep;
